import React, { useState, useCallback, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, Info, X } from 'lucide-react';
import { ToastContext, type ToastMessage, type ToastVariant } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

interface ToastItemProps {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}

const VARIANT_STYLES: Record<ToastVariant, React.CSSProperties> = {
  success: {
    background: 'rgba(74,222,128,0.15)',
    borderLeft: '3px solid #4ADE80',
    color: '#4ADE80',
  },
  error: {
    background: 'rgba(248,113,113,0.15)',
    borderLeft: '3px solid #F87171',
    color: '#F87171',
  },
  info: {
    background: 'var(--color-surface)',
    borderLeft: '3px solid var(--color-primary)',
    color: 'var(--color-text)',
  },
};

const VARIANT_ICONS: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircle size={16} />,
  error: <XCircle size={16} />,
  info: <Info size={16} style={{ color: 'var(--color-primary)' }} />,
};

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Trigger fade-in on next tick
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => onDismiss(toast.id), 200);
  }, [toast.id, onDismiss]);

  const variantStyle = VARIANT_STYLES[toast.variant];
  const iconColor =
    toast.variant === 'success'
      ? '#4ADE80'
      : toast.variant === 'error'
        ? '#F87171'
        : 'var(--color-primary)';

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        ...variantStyle,
        maxWidth: 320,
        borderRadius: 8,
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        boxShadow: 'var(--shadow-md)',
        transition: 'opacity 200ms ease, transform 200ms ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        border: `1px solid ${variantStyle.borderLeft?.toString().replace('3px solid ', '') ?? 'transparent'}`,
        borderLeft: variantStyle.borderLeft,
      }}
    >
      <span style={{ color: iconColor, flexShrink: 0, marginTop: 1 }}>
        {VARIANT_ICONS[toast.variant]}
      </span>
      <span
        style={{
          fontSize: 13,
          lineHeight: 1.5,
          flex: 1,
          color: toast.variant === 'info' ? 'var(--color-text)' : variantStyle.color,
        }}
      >
        {toast.message}
      </span>
      <button
        onClick={handleDismiss}
        className={cn('flex-shrink-0 rounded p-0.5 cursor-pointer')}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--color-text-secondary)',
          marginTop: 1,
          cursor: 'pointer',
        }}
        aria-label="Dismiss notification"
      >
        <X size={14} />
      </button>
    </div>
  );
}

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const toast = useCallback(
    (message: string, variant: ToastVariant = 'info') => {
      const id = Math.random().toString(36).slice(2);
      const newToast: ToastMessage = { id, message, variant };
      setToasts((prev) => [...prev, newToast]);

      const timer = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        timersRef.current.delete(id);
      }, 3000);
      timersRef.current.set(id, timer);
    },
    []
  );

  // Cleanup all timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Toast stack — fixed, above TabBar (bottom 80px), right side */}
      <div
        aria-label="Notifications"
        style={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: 8,
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => (
          <div key={t.id} style={{ pointerEvents: 'auto' }}>
            <ToastItem toast={t} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
